import React, { useState, useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Button,
  useTheme,
  useMediaQuery,
  Tooltip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Assignment as AssignmentIcon,
  Upload as UploadIcon,
  Assessment as AssessmentIcon,
  Search as SearchIcon,
  Menu as MenuIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Analytics as AnalyticsIcon,
  Group as GroupIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';

const drawerWidth = 84; // Optimized width for mini sidebar

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    backgroundColor: '#ffffff',
    borderRight: '1px solid rgba(226, 232, 240, 0.8)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: theme.spacing(2, 0),
    overflowX: 'hidden',
    boxShadow: '4px 0 24px rgba(15, 23, 42, 0.02)',
  },
}));

const NavItem = styled(Box)(({ theme, active }) => ({
  width: 48,
  height: 48,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  color: active ? '#ffffff' : '#94A3B8',
  backgroundColor: active ? theme.palette.primary.main : 'transparent',
  '&:hover': {
    backgroundColor: active ? theme.palette.primary.main : 'rgba(79, 70, 229, 0.05)',
    color: active ? '#ffffff' : theme.palette.primary.main,
    transform: 'translateY(-2px)',
  },
  '& svg': {
    fontSize: 24,
  },
}));

const ActivePill = styled(motion.div)(({ theme }) => ({
  position: 'absolute',
  left: -18,
  width: 4,
  height: 24,
  backgroundColor: theme.palette.primary.main,
  borderRadius: '0 4px 4px 0',
}));

const navItems = [
  { label: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard' },
  { label: 'Recruiters', icon: <GroupIcon />, path: '/recruiters' },
  { label: 'Jobs', icon: <WorkIcon />, path: '/jobs' },
  { label: 'Submissions', icon: <AssignmentIcon />, path: '/submissions' },
  { label: 'Resume Upload', icon: <UploadIcon />, path: '/resume-upload' },
  { label: 'ATS Score', icon: <AssessmentIcon />, path: '/ats-score' },
  { label: 'Search', icon: <SearchIcon />, path: '/search' },
  { label: 'Profiles', icon: <PeopleIcon />, path: '/profiles' },
];

const Navbar = () => {
  const { logout, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const handleNavigation = (path) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    navigate(path);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <StyledDrawer variant="permanent">
      {/* Logo Section */}
      <Box
        sx={{
          mb: 4,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 48,
          height: 48,
          borderRadius: 12,
          background: 'linear-gradient(135deg, #4F46E5 0%, #8B5CF6 100%)',
          boxShadow: '0 8px 16px rgba(79, 70, 229, 0.25)',
        }}
        onClick={() => navigate('/dashboard')}
      >
        <Typography variant="h6" sx={{ color: 'white', fontWeight: 900, fontSize: '1.2rem' }}>
          A
        </Typography>
      </Box>

      {/* Main Navigation */}
      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Tooltip key={item.path} title={item.label} placement="right" arrow>
              <NavItem
                active={isActive}
                onClick={() => handleNavigation(item.path)}
              >
                {isActive && (
                  <ActivePill
                    layoutId="activeNav"
                    transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                  />
                )}
                {item.icon}
              </NavItem>
            </Tooltip>
          );
        })}
      </Box>

      {/* Bottom Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mb: 1 }}>
        <Tooltip title="User Profile" placement="right" arrow>
          <Avatar
            sx={{
              width: 42,
              height: 42,
              cursor: 'pointer',
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
              transition: 'all 0.3s ease',
              '&:hover': { transform: 'scale(1.1)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' }
            }}
            onClick={() => navigate('/user-profile')}
          >
            {user?.email?.[0]?.toUpperCase()}
          </Avatar>
        </Tooltip>

        <Tooltip title="Logout" placement="right" arrow>
          <IconButton
            onClick={handleLogout}
            sx={{
              color: '#94A3B8',
              '&:hover': { color: '#EF4444', backgroundColor: 'rgba(239, 68, 68, 0.05)' }
            }}
          >
            <LogoutIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </StyledDrawer>
  );
};

export default Navbar;
